import { useState } from 'react';
import { cn } from '../../../../../../lib/utils';
import { lightRedBG, primaryText, redTextColor, secondaryText, semiBoldText, smallTextSize } from '../../../../../../styling/stylingHelper';
import type { BorkCostPriceCheck, BorkDayResult, BorkFetchSummary } from '../../../../../../services/admin/adminService';
import { TrivecSummaryChip } from './panelPrimitives';
import type { TFn } from '../../types';

/** Incl-VAT is the reconciliation axis (the API and the report only agree to the cent there). */
const DELTA_TOLERANCE = 0.05;

type Tone = 'ok' | 'warn' | 'mismatch' | 'bad' | 'neutral';

const TONE_CLS: Record<Tone, string> = {
  ok: 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300',
  warn: 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300',
  mismatch: 'bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300',
  bad: 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300',
  neutral: 'bg-gray-100 dark:bg-gray-700/60 text-gray-600 dark:text-gray-300',
};

const STATUS_KEYS: Record<BorkDayResult['status'], string> = {
  exists: 'adminTesting.bork.statusExists',
  missing: 'adminTesting.bork.statusMissing',
  no_section_match: 'adminTesting.bork.statusNoSectionMatch',
  tunnel_down: 'adminTesting.bork.statusTunnelDown',
  error: 'adminTesting.bork.statusError',
};

type Applied = NonNullable<BorkDayResult['applied']>;

const APPLIED_KEYS: Record<Applied, string> = {
  inserted: 'adminTesting.bork.badgeWritten',
  replaced: 'adminTesting.bork.badgeReplaced',
  removed: 'adminTesting.bork.badgeRemoved',
};

/** `removed` deleted a stored row and put nothing back, so it reads red instead of sharing the
 *  sky "we wrote something here" badge. */
const APPLIED_CLS: Record<Applied, string> = {
  inserted: 'bg-sky-100 dark:bg-sky-900/30 text-sky-700 dark:text-sky-300',
  replaced: 'bg-sky-100 dark:bg-sky-900/30 text-sky-700 dark:text-sky-300',
  removed: cn(lightRedBG, redTextColor),
};

const eur = (n: number) => `€${n.toFixed(2)}`;

/**
 * Selection key of a restaurant-day. A day arrives as one row per source type but is written as a
 * whole, so the 3 rows of a day always share one checkbox — and one key on the apply request.
 */
export const borkDayKey = (r: Pick<BorkDayResult, 'restaurantId' | 'date'>) => `${r.restaurantId}|${r.date}`;

/**
 * Only `missing` (apply can insert it) and `exists` (override can replace it) leave anything to
 * write, and only when the API actually returned revenue: a day the POS reports as zero also comes
 * back `exists`, and overriding one of those would delete a real stored row and put nothing in its
 * place. A day is applicable when ANY of its source rows is, so a zero FOOD row next to a non-zero
 * DRINKS row still selects the day.
 */
export const isApplicableRow = (r: BorkDayResult) =>
  (r.status === 'missing' || r.status === 'exists') && r.borkTotalInc > 0;

/** Every restaurant-day of the run that has at least one writable source row. */
export function applicableDayKeys(summary: BorkFetchSummary): string[] {
  const keys = new Set<string>();
  for (const r of summary.results) if (isApplicableRow(r)) keys.add(borkDayKey(r));
  return Array.from(keys);
}

/** Wiring for the per-day checkboxes; omitted when the table is read-only. */
export interface BorkResultsSelection {
  /** `borkDayKey` of every picked day. */
  selected: Set<string>;
  onToggleDay: (dayKey: string) => void;
  /** Every applicable day, green ones included — a full-range override for name normalisation. */
  onSelectAll: () => void;
  /** The issue-day rule, which is also the state a fresh dry run lands in. */
  onSelectIssues: () => void;
  onSelectNone: () => void;
}

/** Breaking names shown inline before the list collapses behind a toggle. */
const BREAKING_PREVIEW = 5;

/**
 * One restaurant's verdict on whether an override is safe for its cost-price links. Green means
 * every linked item name survives the pull; red lists the names that would stop matching, because
 * that is the decision the operator has to make BEFORE writing, not after.
 */
function BorkCostPriceCheckLine({ check, t }: { check: BorkCostPriceCheck; t: TFn }) {
  const [expanded, setExpanded] = useState(false);
  const safe = check.breaking.length === 0;
  const shown = expanded ? check.breaking : check.breaking.slice(0, BREAKING_PREVIEW);
  const hidden = check.breaking.length - shown.length;

  return (
    <div className={cn('rounded-lg border px-3 py-2',
      safe
        ? 'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20'
        : 'border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20')}>
      <div className={cn('text-xs', semiBoldText, safe ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300')}>
        {check.restaurantName}
      </div>
      <div className={cn('text-xs', safe ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300')}>
        {safe
          ? t('adminTesting.bork.costPriceOk', { linked: check.linkedCount, newlyLinked: check.newlyLinked.length })
          : t('adminTesting.bork.costPriceBreaking', { breaking: check.breaking.length, linked: check.linkedCount })}
      </div>
      {!safe && (
        <div className="mt-1 flex flex-wrap items-center gap-1">
          {shown.map((name) => (
            <span key={name} className={cn('px-2 py-0.5 rounded-full font-semibold text-xs break-words', TONE_CLS.bad)}>
              {name}
            </span>
          ))}
          {(hidden > 0 || expanded) && (
            <button
              onClick={() => setExpanded((v) => !v)}
              className={cn('underline underline-offset-2 text-xs', 'text-red-700 dark:text-red-300')}
            >
              {expanded ? t('adminTesting.bork.costPriceShowLess') : t('adminTesting.bork.costPriceShowAll', { count: hidden })}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/** Added/removed category names for the `±categories` chip's tooltip. */
function categoryDiffTitle(r: BorkDayResult, t: TFn): string {
  const parts: string[] = [];
  if (r.categoriesAdded?.length) parts.push(t('adminTesting.bork.categoriesAdded', { names: r.categoriesAdded.join(', ') }));
  if (r.categoriesRemoved?.length) parts.push(t('adminTesting.bork.categoriesRemoved', { names: r.categoriesRemoved.join(', ') }));
  return parts.join('\n');
}

/** Stored minus Bork, incl VAT — only meaningful once a stored row exists. */
const deltaOf = (r: BorkDayResult): number | null =>
  r.storedTotalInc === undefined ? null : r.storedTotalInc - r.borkTotalInc;

/**
 * A row that still needs action: not stored at all, or storing a total that disagrees with the API
 * beyond the tolerance. An `applied` flag means this run already wrote it, which resolves the row
 * whatever its pre-write status said — that is what keeps a day from staying selected after the
 * operator has already fixed it.
 */
export const isIssueRow = (r: BorkDayResult): boolean => {
  if (r.applied) return false;
  if (r.status === 'missing') return true;
  const delta = deltaOf(r);
  return delta !== null && Math.abs(delta) > DELTA_TOLERANCE;
};

/**
 * The default selection: restaurant-days with at least one unresolved issue row, intersected with
 * the applicability rule so a day nothing can be written to is never pre-ticked. All-green stored
 * days are deliberately left out — the operator reaches those through "select all".
 */
export function issueDayKeys(summary: BorkFetchSummary): string[] {
  const applicable = new Set(applicableDayKeys(summary));
  const keys = new Set<string>();
  for (const r of summary.results) {
    const key = borkDayKey(r);
    if (applicable.has(key) && isIssueRow(r)) keys.add(key);
  }
  return Array.from(keys);
}

/**
 * `no_section_match` is red, not amber: the source type carries revenue and has no revenue source
 * to write it to (only a failed auto-create gets here), so the money has nowhere to land.
 */
function rowTone(r: BorkDayResult): Tone {
  // A removed row's stored-vs-API delta was just resolved by deleting the stored row — painting it
  // as a mismatch would tell the operator something is still wrong. Grey rather than green: the
  // status stopped meaning anything here, and the red `removed` badge carries what happened.
  if (r.applied === 'removed') return 'neutral';
  if (r.status === 'error' || r.status === 'tunnel_down' || r.status === 'no_section_match') return 'bad';
  if (r.status === 'missing') return 'warn';
  const delta = deltaOf(r);
  return delta !== null && Math.abs(delta) > DELTA_TOLERANCE ? 'mismatch' : 'ok';
}

export function BorkFetchResults({ summary, selection, t }: { summary: BorkFetchSummary; selection?: BorkResultsSelection; t: TFn }) {
  const { mode } = summary;
  const rows = [...summary.results].sort((a, b) =>
    a.restaurantName.localeCompare(b.restaurantName) ||
    a.date.localeCompare(b.date) ||
    (a.sourceType ?? '').localeCompare(b.sourceType ?? '')
  );

  // The sort groups every source row of a day together, so a day's checkbox is drawn once on its
  // first row and spans the rest.
  const daySpans = new Map<string, number>();
  const applicableDays = new Set<string>();
  for (const r of rows) {
    const key = borkDayKey(r);
    daySpans.set(key, (daySpans.get(key) ?? 0) + 1);
    if (isApplicableRow(r)) applicableDays.add(key);
  }
  const firstRowOfDay = new Set<number>();
  const seenDays = new Set<string>();
  rows.forEach((r, i) => {
    const key = borkDayKey(r);
    if (!seenDays.has(key)) { seenDays.add(key); firstRowOfDay.add(i); }
  });

  const missing = rows.filter((r) => r.status === 'missing').length;
  const exists = rows.filter((r) => r.status === 'exists').length;
  const mismatches = rows.filter((r) => rowTone(r) === 'mismatch').length;
  const errors = rows.filter((r) => r.status === 'error' || r.status === 'tunnel_down').length;
  const noSectionMatch = rows.filter((r) => r.status === 'no_section_match').length;

  const cellCls = 'px-2 py-1.5 align-top';
  const headCls = cn('px-2 py-1.5 text-left font-semibold', secondaryText, 'text-xs');

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className={cn('px-2 py-0.5 rounded-full font-semibold',
          mode === 'dry'
            ? 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
            : mode === 'override'
            ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
            : 'bg-sky-100 dark:bg-sky-900/30 text-sky-700 dark:text-sky-300')}>
          {t(mode === 'dry' ? 'adminTesting.bork.modeDry' : mode === 'override' ? 'adminTesting.bork.modeOverride' : 'adminTesting.bork.modeApply')}
        </span>
        <span className={secondaryText}>
          {t('adminTesting.bork.resultSummary', {
            venues: summary.venues,
            days: summary.restaurantDays,
            start: summary.startDate,
            end: summary.endDate,
          })}
        </span>
      </div>

      <div className="flex flex-wrap gap-2">
        <TrivecSummaryChip label={t('adminTesting.bork.countMissing')} value={missing} />
        <TrivecSummaryChip label={t('adminTesting.bork.countExists')} value={exists} />
        <TrivecSummaryChip label={t('adminTesting.bork.countMismatch')} value={mismatches} />
        <TrivecSummaryChip label={t('adminTesting.bork.countNoSectionMatch')} value={noSectionMatch} />
        <TrivecSummaryChip label={t('adminTesting.bork.countErrors')} value={errors} />
        {(mode !== 'dry' || summary.inserted > 0) && <TrivecSummaryChip label={t('adminTesting.bork.countWritten')} value={summary.inserted} />}
        {(mode === 'override' || summary.replaced > 0) && <TrivecSummaryChip label={t('adminTesting.bork.countReplaced')} value={summary.replaced} />}
        {summary.removed > 0 && <TrivecSummaryChip label={t('adminTesting.bork.countRemoved')} value={summary.removed} />}
      </div>

      {summary.costPriceChecks && summary.costPriceChecks.length > 0 && (
        <div className="space-y-1.5">
          {/* The caveats that make the numbers readable (range scope, changes-not-health, the
              whole-range assumption behind `breaking`) ride in the tooltip so the line stays short. */}
          <div className={cn(secondaryText, 'text-xs cursor-help', semiBoldText)} title={t('adminTesting.bork.costPriceScopeTitle')}>
            {t('adminTesting.bork.costPriceTitle')}
          </div>
          {summary.costPriceChecks.map((c) => (
            <BorkCostPriceCheckLine key={c.restaurantId} check={c} t={t} />
          ))}
        </div>
      )}

      {selection && rows.length > 0 && (
        <div className="flex flex-wrap items-center gap-3">
          <button onClick={selection.onSelectAll} className={cn('underline underline-offset-2', secondaryText, 'text-xs')}>
            {t('adminTesting.bork.selectAll')}
          </button>
          <button onClick={selection.onSelectIssues} className={cn('underline underline-offset-2', secondaryText, 'text-xs')}>
            {t('adminTesting.bork.selectIssues')}
          </button>
          <button onClick={selection.onSelectNone} className={cn('underline underline-offset-2', secondaryText, 'text-xs')}>
            {t('adminTesting.bork.selectNone')}
          </button>
          <span className={cn(secondaryText, 'text-xs')}>
            {t('adminTesting.bork.selectedCount', { selected: selection.selected.size, total: applicableDays.size })}
          </span>
          <span className={cn(secondaryText, 'text-xs italic')}>{t('adminTesting.bork.selectionDefaultHint')}</span>
        </div>
      )}

      {rows.length === 0 ? (
        <div className={cn(secondaryText, smallTextSize, 'text-center py-4')}>{t('adminTesting.bork.noResults')}</div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
          <table className="w-full text-xs">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                {selection && <th className={cn(headCls, 'w-8')}><span className="sr-only">{t('adminTesting.bork.colSelect')}</span></th>}
                <th className={headCls}>{t('adminTesting.bork.colRestaurant')}</th>
                <th className={headCls}>{t('adminTesting.bork.colDate')}</th>
                <th className={headCls}>{t('adminTesting.bork.colSourceType')}</th>
                <th className={headCls}>{t('adminTesting.bork.colStatus')}</th>
                <th className={cn(headCls, 'text-right')}>{t('adminTesting.bork.colBorkTotal')}</th>
                <th className={cn(headCls, 'text-right')}>{t('adminTesting.bork.colStoredTotal')}</th>
                <th className={cn(headCls, 'text-right')}>{t('adminTesting.bork.colDelta')}</th>
                <th className={headCls}>{t('adminTesting.bork.colUnmatched')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const tone = rowTone(r);
                const delta = deltaOf(r);
                const dayKey = borkDayKey(r);
                return (
                  <tr key={`${r.restaurantId}:${r.date}:${r.sourceType ?? 'venue'}`} className="border-t border-gray-200 dark:border-gray-700">
                    {selection && firstRowOfDay.has(i) && (
                      <td className={cellCls} rowSpan={daySpans.get(dayKey)}>
                        <input
                          type="checkbox"
                          checked={selection.selected.has(dayKey)}
                          disabled={!applicableDays.has(dayKey)}
                          onChange={() => selection.onToggleDay(dayKey)}
                          title={applicableDays.has(dayKey) ? undefined : t('adminTesting.bork.dayNotApplicable')}
                          aria-label={t('adminTesting.bork.selectDayAria', { restaurant: r.restaurantName, date: r.date })}
                          className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-sky-600 dark:text-sky-600 focus:ring-sky-500 dark:focus:ring-sky-500 disabled:opacity-40 disabled:cursor-not-allowed"
                        />
                      </td>
                    )}
                    <td className={cn(cellCls, primaryText, semiBoldText)}>{r.restaurantName}</td>
                    <td className={cn(cellCls, secondaryText, 'tabular-nums whitespace-nowrap')}>{r.date}</td>
                    <td className={cn(cellCls, secondaryText)}>{r.sourceType ?? '—'}</td>
                    <td className={cellCls}>
                      <div className="flex flex-wrap items-center gap-1">
                        <span className={cn('px-2 py-0.5 rounded-full font-semibold whitespace-nowrap', TONE_CLS[tone])}>
                          {t(STATUS_KEYS[r.status])}
                        </span>
                        {r.applied && (
                          <span className={cn('px-2 py-0.5 rounded-full font-semibold whitespace-nowrap', APPLIED_CLS[r.applied])}>
                            {t(APPLIED_KEYS[r.applied])}
                          </span>
                        )}
                        {!!(r.categoriesAdded?.length || r.categoriesRemoved?.length) && (
                          <span
                            className={cn('px-2 py-0.5 rounded-full font-semibold whitespace-nowrap cursor-help', TONE_CLS.warn)}
                            title={categoryDiffTitle(r, t)}
                          >
                            {t('adminTesting.bork.categoryDiffChip', {
                              added: r.categoriesAdded?.length ?? 0,
                              removed: r.categoriesRemoved?.length ?? 0,
                            })}
                          </span>
                        )}
                      </div>
                      {r.error && <div className="mt-1 text-red-700 dark:text-red-300 break-words">{r.error}</div>}
                    </td>
                    <td
                      className={cn(cellCls, primaryText, 'text-right tabular-nums whitespace-nowrap')}
                      title={t('adminTesting.bork.borkTotalTitle', { ex: eur(r.borkTotalEx), day: eur(r.dayTotalInc) })}
                    >
                      {eur(r.borkTotalInc)}
                    </td>
                    <td className={cn(cellCls, secondaryText, 'text-right tabular-nums whitespace-nowrap')}>
                      {r.storedTotalInc === undefined ? '—' : eur(r.storedTotalInc)}
                    </td>
                    {/* Same suppression as the status chip: a rose delta on a removed row would
                        re-flag a divergence the deletion already resolved. */}
                    <td className={cn(cellCls, 'text-right tabular-nums whitespace-nowrap font-semibold',
                      delta === null || r.applied === 'removed'
                        ? secondaryText
                        : Math.abs(delta) > DELTA_TOLERANCE
                        ? 'text-rose-700 dark:text-rose-300'
                        : 'text-green-700 dark:text-green-300')}>
                      {delta === null ? '—' : eur(delta)}
                    </td>
                    <td className={cellCls}>
                      {r.unmatchedCategories.length > 0 ? (
                        <span className={cn('inline-block px-2 py-0.5 rounded-full font-semibold', TONE_CLS.warn)}>
                          {r.unmatchedCategories.join(', ')}
                        </span>
                      ) : (
                        <span className={secondaryText}>—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
